import axios from 'axios';
import { PAYMENT_PROVIDERS_ENUM } from '../utils/constants';
import { getImageDimensions } from '../utils/imageHelpers';
import { getProductInstanceId } from '../helpers/cookieHelper';

class ApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const CHECKOUT_API_BASE_URL = process.env.REACT_APP_CHECKOUT_APP_API_BASE_URL;
const BUILDERSERVICES_API_BASE_URL = process.env.REACT_APP_BUILDERSERVICES_API_BASE_URL;
const SITEPLUS_API_BASE_URL = process.env.REACT_APP_SITEPLUS_API_BASE_URL;
export const IMAGE_API_BASE_URL = (process.env.REACT_APP_IMAGE_API_BASE_URL || '').replace(/\/+$/, '');
const API_BASE_URL = CHECKOUT_API_BASE_URL;
const SF_CORE_ACCESS_KEY = process.env.REACT_APP_SF_CORE_ACCESS_KEY;
const SF_CORE_INFO_API_KEY = process.env.REACT_APP_SF_CORE_INFO_API_KEY;
const PRODUCT_INSTANCE_ID = getProductInstanceId();
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    // Don't add token for getToken request (sfcore.do)
    const isGetTokenRequest = config.url?.includes('sfcore.do');

    if (!isGetTokenRequest) {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      return Promise.reject({
        message: error.response.data?.message || 'An error occurred',
        status: error.response.status,
      });
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: 0,
      });
    } else {
      // Something else happened
      return Promise.reject({
        message: error.message || 'An unexpected error occurred',
        status: 0,
      });
    }
  }
);

const imageApiClient = axios.create({
  baseURL: IMAGE_API_BASE_URL || undefined,
  headers: {
    'Content-Type': 'application/json',
  },
});

imageApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

imageApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      return Promise.reject(
        new ApiError(
          error.response.data?.message ||
          error.response.data?.error ||
          'An error occurred',
          error.response.status
        )
      );
    }
    if (error.request) {
      return Promise.reject(
        new ApiError('Network error. Please check your connection.', 0)
      );
    }
    return Promise.reject(
      new ApiError(error.message || 'An unexpected error occurred', 0)
    );
  }
);

/** Plain axios for GCS signed URLs — no Bearer token. */
const gcsClient = axios.create({
  maxRedirects: 0,
  validateStatus: (status) => status >= 200 && status < 400,
});

function normalizeGcsAxiosError(error, defaultMessage) {
  if (error instanceof ApiError) {
    return error;
  }
  if (error?.response) {
    return new ApiError(defaultMessage, error.response.status);
  }
  if (error?.request) {
    return new ApiError(
      'Network error during upload. Please check your connection.',
      0
    );
  }
  return new ApiError(error?.message || defaultMessage, 0);
}

function flattenContentHeaders(contentHeaders) {
  const headers = {};
  if (!contentHeaders || typeof contentHeaders !== 'object') {
    return headers;
  }
  Object.keys(contentHeaders).forEach((key) => {
    const val = contentHeaders[key];
    const str = Array.isArray(val) ? val[0] : val;
    if (str !== undefined && str !== null && String(str) !== '') {
      headers[key] = String(str);
    }
  });
  return headers;
}

function extractUploadIdFromLocation(locationHeader, baseUrlForRelative) {
  if (!locationHeader || typeof locationHeader !== 'string') {
    return null;
  }
  try {
    const base = baseUrlForRelative || 'https://storage.googleapis.com/';
    const absolute = new URL(locationHeader.trim(), base).href;
    const u = new URL(absolute);
    const id = u.searchParams.get('upload_id');
    if (id) return id;
  } catch {
    // fall through to regex
  }
  const match = String(locationHeader).match(/upload_id=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function appendUploadIdToSignedUrl(signedUrl, uploadId) {
  const joiner = signedUrl.includes('?') ? '&' : '?';
  return `${signedUrl}${joiner}upload_id=${encodeURIComponent(uploadId)}`;
}

async function requestImageSignedUrls(items) {
  const response = await imageApiClient.post('v1.0/images/signed_urls', items);
  const data = response.data;
  if (!Array.isArray(data) || data.length === 0) {
    throw new ApiError('Invalid response from image service.', 0);
  }
  return data;
}

async function initiateGcsResumableUpload(signedUrlString, contentHeaders) {
  try {
    const headers = flattenContentHeaders(contentHeaders);
    const res = await gcsClient.post(signedUrlString, null, { headers });
    const location =
      res.headers?.location ||
      res.headers?.Location ||
      res.headers?.['location'];
    let uploadId = extractUploadIdFromLocation(location, signedUrlString);
    if (!uploadId && res.data && typeof res.data === 'object' && res.data.upload_id) {
      uploadId = res.data.upload_id;
    }
    if (!uploadId) {
      throw new ApiError(
        'Could not start upload (missing upload session).',
        res.status || 0
      );
    }
    return { uploadId, sessionLocation: location ? String(location).trim() : null };
  } catch (e) {
    if (e instanceof ApiError) {
      throw e;
    }
    throw normalizeGcsAxiosError(e, 'Could not start file upload to storage.');
  }
}

async function putGcsFile(signedUrlString, uploadId, file, sessionLocation, onUploadProgress) {
  try {
    const putUrl = sessionLocation
      ? new URL(sessionLocation, signedUrlString).href
      : appendUploadIdToSignedUrl(signedUrlString, uploadId);
    const size = file.size;
    const lastByte = size > 0 ? size - 1 : 0;
    const headers = {
      'Content-Type': file.type || 'application/octet-stream',
      'Content-Length': size,
    };
    if (size > 0) {
      headers['Content-Range'] = `bytes 0-${lastByte}/${size}`;
    }
    await gcsClient.put(putUrl, file, {
      headers,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress,
    });
  } catch (e) {
    if (e instanceof ApiError) {
      throw e;
    }
    throw normalizeGcsAxiosError(e, 'Could not upload file to storage.');
  }
}

function pickImageUrlFromCallbackResponse(data) {
  const candidates = Array.isArray(data)
    ? data
    : data?.data && Array.isArray(data.data)
      ? data.data
      : [data];
  const first =
    candidates.find(
      (item) =>
        item &&
        (item.if_success_image_url || item.image_url || item.url || item.public_url)
    ) || candidates[0];
  if (!first || typeof first !== 'object') {
    return null;
  }
  return (
    first.if_success_image_url ||
    first.image_url ||
    first.url ||
    first.public_url ||
    null
  );
}

async function finalizeImageUpload(imageIds) {
  const response = await imageApiClient.post('v1.0/images/signed_url_callback', {
    image_ids: imageIds,
  });
  const url = pickImageUrlFromCallbackResponse(response.data);
  return { url, raw: response.data };
}

async function withRetry(operation, attempts = 2, baseDelayMs = 400) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await operation();
    } catch (e) {
      lastErr = e;
      const status = e?.status ?? 0;
      const retryable = status === 0 || status >= 500;
      if (!retryable || i === attempts - 1) {
        throw e;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
    }
  }
  throw lastErr;
}

export const api = {
  // Get token
  getToken: async () => {
    try {
      const response = await apiClient.post(
        'https://www.networksolutions.com/sfcore.do?getJWTTokenV1',
        {
          request: {
            productInstanceId: PRODUCT_INSTANCE_ID,
            requestInfo: {
              apiAccessKey: SF_CORE_ACCESS_KEY,
              clientId: 'NSI',
              method: 'getJWTTokenV1',
              service: 'ProductAPI',
            }
          }
        },
        { withCredentials: true }
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },
  getStore: async () => {
    try {
      const response = await apiClient.get(`storedash/settings/v1.0/business-info/store?productInstanceId=${PRODUCT_INSTANCE_ID}`);
      return response.data;
    } catch (error) {
      console.error('Error getting store ID:', error);
      return null;
    }
  },

  /**
   * @param {string|number} siteId
   * @returns {Promise<Array<{ code: string, name: string, symbol: string | null }>>}
   */
  getCurrencies: async (siteId) => {
    try {
      const response = await apiClient.get('booking/v1.0/settings/currencies', {
        params: { siteId },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching currencies:', error);
      return [];
    }
  },

  /**
   * Resumable image upload: signed URL → GCS session → PUT bytes → callback (Bearer on SS API only).
   * @param {File} file
   * @param {(percent: number) => void} [onUploadProgress] - 0–100 while uploading bytes to GCS
   */
  uploadImage: async (file, onUploadProgress) => {
    if (!IMAGE_API_BASE_URL) {
      throw new ApiError(
        'Image API is not configured (missing REACT_APP_IMAGE_API_BASE_URL).',
        0
      );
    }
    if (!file) {
      throw new ApiError('No file selected.', 0);
    }

    const dimensions = await getImageDimensions(file);
    const payloadItem = {
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      content_length: file.size,
      image_width: dimensions.width,
      image_height: dimensions.height,
    };

    const signedRows = await withRetry(() => requestImageSignedUrls([payloadItem]));
    const row = signedRows[0];
    if (!row) {
      throw new ApiError('Invalid response from image service.', 0);
    }
    if (row.success === false) {
      const msg =
        (typeof row.error === 'string' && row.error) ||
        row.error?.message ||
        'Could not request upload URL.';
      throw new ApiError(String(msg), row.result_code || 0);
    }
    const imageId = row.image_id;
    const signed = row.signed_url;
    if (!signed || typeof signed.signed_url !== 'string') {
      throw new ApiError('Invalid upload URL response.', 0);
    }

    const signedUrlString = signed.signed_url;
    const contentHeaders = signed.content_headers;

    const { uploadId, sessionLocation } = await withRetry(() =>
      initiateGcsResumableUpload(signedUrlString, contentHeaders)
    );

    const progressHandler =
      typeof onUploadProgress === 'function'
        ? (progressEvent) => {
          const total = progressEvent.total;
          if (total) {
            onUploadProgress(Math.round((progressEvent.loaded * 100) / total));
          }
        }
        : undefined;

    await withRetry(() =>
      putGcsFile(signedUrlString, uploadId, file, sessionLocation, progressHandler)
    );

    if (typeof onUploadProgress === 'function') {
      onUploadProgress(100);
    }

    const { url: finalUrl } = await withRetry(() => finalizeImageUpload([imageId]));

    if (!finalUrl) {
      throw new ApiError(
        'Upload finished but the image URL was not returned. Please try again.',
        0
      );
    }

    return {
      success: true,
      data: { url: finalUrl, image_id: imageId },
    };
  },

  /**
   * Digital file: signed URL from store settings → GCS resumable init (POST) → PUT bytes.
   * @param {File} file
   * @param {(percent: number) => void} [onUploadProgress]
   */
  uploadDigitalFile: async (file, onUploadProgress) => {
    const storeId = localStorage.getItem('storeId');
    if (!storeId) {
      throw new ApiError('Store ID is not available. Please refresh and try again.', 0);
    }
    if (!file) {
      throw new ApiError('No file selected.', 0);
    }

    const payload = {
      file_name: file.name,
      content_length: file.size,
      content_type: file.type || 'application/octet-stream',
    };

    const signedResponse = await apiClient.post(
      `storedash/settings/v1.0/${storeId}/checkout/digital-signed-url`,
      payload
    );
    const body = signedResponse.data || {};
    const signedUrlString = body.signedUrl || body.signed_url;
    const internalFilename = body.internalFilename || body.internal_filename;
    const contentHeaders = body.contentHeaders || body.content_headers;

    if (!signedUrlString || typeof signedUrlString !== 'string') {
      throw new ApiError('Invalid response from digital signed URL API.', 0);
    }
    if (!internalFilename || typeof internalFilename !== 'string') {
      throw new ApiError('Invalid response from digital signed URL API (missing internalFilename).', 0);
    }

    const { uploadId, sessionLocation } = await withRetry(() =>
      initiateGcsResumableUpload(signedUrlString, contentHeaders)
    );

    const progressHandler =
      typeof onUploadProgress === 'function'
        ? (progressEvent) => {
            const total = progressEvent.total;
            if (total) {
              onUploadProgress(Math.round((progressEvent.loaded * 100) / total));
            }
          }
        : undefined;

    await withRetry(() =>
      putGcsFile(
        signedUrlString,
        uploadId,
        file,
        sessionLocation,
        progressHandler
      )
    );

    if (typeof onUploadProgress === 'function') {
      onUploadProgress(100);
    }

    return {
      success: true,
      data: {
        internalFilename,
        url: internalFilename,
        content_type: file.type || 'application/octet-stream',
        content_length: file.size,
        filename: file.name,
      },
    };
  },

  // Upload file (non-image flows, e.g. digital product file — backend /upload-file)
  uploadFile: async (file, type = 'image', onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await apiClient.post('/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total;
          if (typeof onUploadProgress === 'function' && total) {
            onUploadProgress(Math.round((progressEvent.loaded * 100) / total));
          }
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  // Publish
  publish: async (state) => {
    try {
      const response = await apiClient.post('/publish', state);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  getPaymentProviders: async (storeId) => {
    try {
      const response = await apiClient.get(`storedash/checkout/v1.0/external-payment/${storeId}/payment/list`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  activatePayPal: async (storeId, paypalEmail) => {
    const response = await apiClient.post('storedash/checkout/v1.0/external-payment/settings/activate', {
      store_id: storeId,
      payment_provider: PAYMENT_PROVIDERS_ENUM.PAYPAL,
      update_paypal_settings: {
        paypal_email: paypalEmail,
      },
    });
    return response.data;
  },

  deactivatePayPal: async (storeId) => {
    const response = await apiClient.post('storedash/checkout/v1.0/external-payment/settings/deactivate', {
      store_id: storeId,
      payment_provider: PAYMENT_PROVIDERS_ENUM.PAYPAL,
      update_paypal_settings: {},
    });
    return response.data;
  },

  activateStripe: async (storeId) => {
    const response = await apiClient.post('storedash/checkout/v1.0/external-payment/settings/activate', {
      store_id: storeId,
      payment_provider: PAYMENT_PROVIDERS_ENUM.STRIPE,
    });
    return response.data;
  },

  getStripeConnectUrl: async (storeId) => {
    const token = localStorage.getItem('authToken');
    const endpointPath = `storedash/checkout/v1.0/external-payment/${storeId}/stripe/connect`;
    // Backend contract: respond with 200 OK and a JSON body containing the
    // Stripe OAuth URL (e.g. { redirect_url: "https://connect.stripe.com/..." }).
    // It must NOT 302 to Stripe directly, because XHR-followed cross-origin
    // redirects to connect.stripe.com fail CORS for some users (notably IN).
    const response = await apiClient.post(
      endpointPath,
      token,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  deactivateStripe: async (storeId) => {
    const response = await apiClient.post('storedash/checkout/v1.0/external-payment/settings/deactivate', {
      store_id: storeId,
      payment_provider: PAYMENT_PROVIDERS_ENUM.STRIPE,
    });
    return response.data;
  },

  createProduct: async (productData) => {
    try {
      const response = await apiClient.post(`${BUILDERSERVICES_API_BASE_URL}/storedash-products-api/v1.0/rest/CheckoutProduct/create-product`, productData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  saveBusinessInfo: async (businessInfo) => {
    try {
      const response = await apiClient.post(`${BUILDERSERVICES_API_BASE_URL}/storedash-settings-api/v1.0/business-info`, businessInfo);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  createCheckoutPage: async (createCheckoutPagePayload) => {
    try {
      const response = await apiClient.post(`${SITEPLUS_API_BASE_URL}/projects/lw-checkout?productInstanceId=${PRODUCT_INSTANCE_ID}`, createCheckoutPagePayload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  updateCheckoutPage: async (projectId, appInstanceId, productId) => {
    try {
      const response = await apiClient.put(
        `${SITEPLUS_API_BASE_URL}/projects/lw-checkout?productInstanceId=${PRODUCT_INSTANCE_ID}`,
        {
          projectId,
          data: {
            storeId: appInstanceId,
            productId,
          },
        }
      );
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      throw error;
    }
  },

  publishCheckoutPage: async () => {
    try {
      const response = await apiClient.post(`${SITEPLUS_API_BASE_URL}/projects/lw-checkout/publish?productInstanceId=${PRODUCT_INSTANCE_ID}`, {});
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  },

  getBusinessInfo: async (connectedDomain) => {
    try {
      const response = await apiClient.post(
        'https://www.networksolutions.com/sfcore.do?getAllWhoisContactsForDomain',
        {
          "request": {
            "requestInfo": {
              "service": "DomainAPI",
              "method": "getAllWhoisContactsForDomain",
              "clientId": "AccountManager",
              "apiAccessKey": SF_CORE_INFO_API_KEY
            },
            "domainName": connectedDomain,
            "registrantPersonOrgId": 0
          }
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMfeAccount: async () => {
    try {
      const response = await apiClient.get(
        `${SITEPLUS_API_BASE_URL}/account/mfe?productInstanceId=${PRODUCT_INSTANCE_ID}`,
        // { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};